// OriginsListMobile.tsx
import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Checkbox, Divider, IconButton, Stack, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef, type GridRenderCellParams, type GridRowIdGetter } from '@mui/x-data-grid';
import { api } from "../../../api/originApi";
import { useFetch } from "../../../hooks/useFetch";
import { usePresetDialogs } from '../../dialog';
import { FormModal } from '../../modal/FormModal';
import ActionButtons, { ActionButtonProp } from '../../ActionButtons';
import OriginForm from './OriginForm';
import { Origin } from '../../../types/origin';
import { OriginType } from '../../../types/originType';
import { Entity } from '../../../types/entity';
import { useTranslation } from 'react-i18next';
import { ImportForm } from './ImportForm';
import LocalizedGrid from '../../localizedGrid/LocalizedGridCook3';
import { Assessment, Delete, Edit, MoreHoriz } from '@mui/icons-material';
import i18n from '../../../i18n';
import { useAuthStore } from '../../../store/useAuthStore3';

export const OriginsList: React.FC = () => {
    const { t } = useTranslation();
    const { data, setData, error } = useFetch<Origin[]>(api.get, []);
    const { data: originTypes, } = useFetch<OriginType[]>(api.getOriginTypes, []);
    const { data: parents, } = useFetch<Entity[]>(api.getEntities, []);
    const { customerId } = useAuthStore();
    const { showInfo, showConfirm, showPrompt, showOptions, DialogComponent } = usePresetDialogs();
    const [selectedOrigin, setSelectedOrigin] = useState<Origin | null>(null);    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const [totalEnabled, setTotalEnabled] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    useEffect(() => {
        const enabledCount = data.filter(item => item.is_enabled).length;
        setTotalEnabled(enabledCount);
    }, [data]);

    if (!customerId) {
        return <Typography>t('customer.loading')</Typography>;
    }    

    const columns: GridColDef[] = [
        { field: "entity_id", headerName: "ID", type: "number", flex: 1, width: 50, },
        { field: "name", headerName: t("OriginForm.Name_Label"), type: "string", flex: 2, width: 200, },
        { field: "origin_type_id", headerName: t("OriginForm.Origin_Type_Label"), type: "string", flex: 2, width: 100, display: 'flex',
            renderCell: (params: GridRenderCellParams<Origin>) => {
                return (
                    <Typography>
                        {originTypes.find(ot => ot.origin_type_id === params.row.origin_type_id)?.name}
                    </Typography>
                );
            }
        },
        { field: "parent_id", headerName: t("OriginForm.Parent_Label"), type: "string", flex: 2, width: 200,  display: 'flex',
            renderCell: (params: GridRenderCellParams<Origin>) => {
                return (
                    <Typography>
                        {parents.find(p => p.entity_id === params.row.parent_id)?.name}
                    </Typography>
                );
            }
        },
        { field: 'is_enabled',
            headerName: t('OriginForm.enabled'),
            width: 120,
            renderCell: (params) => {
                let backgroundColor;
                if (params.value === true) {
                    backgroundColor = 'green';
                } else if (params.value === false) {
                    backgroundColor = 'red';
                }
                return (
                    <Box
                    sx={{
                        mt: 2,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: backgroundColor,
                    }}
                    />
                );
            },      
        },
        { field: 'success',
            headerName: t('OriginForm.success'),
            width: 120,
            renderCell: (params) => {
                let backgroundColor;
                if (params.value === true) {
                    backgroundColor = 'green';
                } else if (params.value === false) {
                    backgroundColor = 'red';
                } else {
                    backgroundColor = 'lightgray';
                }
                return (
                    <Box
                    sx={{
                        mt: 2,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: backgroundColor,
                        // backgroundColor: SelectByBool(params.value, 'green', 'red', 'lightgray'),
                    }}
                    />
                );
            },      
        },
        {
            field: 'actions',
            // headerName: 'Actions',
            headerName: t('common.actions'),
            width: 150,
            sortable: false,
            renderCell: (params: GridRenderCellParams<Origin>) => {
                return (
                    <ActionButtons actions={[
                        {type: 'edit', action: () => handleOpenModal(params.row), },
                        {type: 'delete', action: () => handleDelete(params.row.entity_id), },
                        {type: 'extended', action: () => handleExtended(params.row), },
                    ]} />
                );
            },
        },        
    ];

    const handleUpdate = async (data: Origin) => {
        if (!!selectedOrigin) {
            await api.put(data);
            setData((prev) => prev.map(origin => origin.entity_id === data.entity_id ? data : origin));
        } else {
            const newData = await api.post(data);
            setData((prev) => [...prev, newData]);
        }
        handleCloseModal();
    };

    const handleDelete = async (id: number) => {
        const result = await showConfirm(
            t('OriginsList.Are_you_sure_you_want_to_delete_the_camera', {id}), 
            t('common.delete_confirmation'));
            //
        if (!result) return;

        await api.delete(id);

        const updatedPoints = data.filter(origin => id !== origin.entity_id);
        setData(updatedPoints);
        console.log(`Origin with ID ${id} deleted.`);
    };

    const handleExtended = async (origin: Origin) => {
        setSelectedOrigin(origin);
        // todo: show import form
        setIsImportOpen(true);
    }
    
    const handleOpenModal = (origin: Origin | null) => {
        setSelectedOrigin(origin);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);

    const handleCloseImport = () => setIsImportOpen(false);

// --- Рендер карточки для мобильных устройств ---
    const renderMobileCard = (origin: Origin) => (
        <Card key={origin.entity_id} sx={{ mb: 2, boxShadow: 2 }}>
            <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" color="primary">
                        {origin.name}
                    </Typography>
                    <Box>
                        <IconButton onClick={() => handleOpenModal(origin)} size="small"><Edit /></IconButton>
                        {/* <IconButton onClick={() => { setSelectedPlate(plate); setIsExtendedOpen(true); }} size="small"><Assessment /></IconButton> */}
                        <IconButton onClick={() => handleDelete(origin.entity_id)} size="small" color="error"><Delete /></IconButton>
                        <IconButton onClick={() => handleExtended(origin)} size="small" ><MoreHoriz /></IconButton>
                    </Box>
                </Box>
                
                <Divider sx={{ my: 1 }} />
                
                <Stack spacing={0.5}>
                    <Typography variant="body2"><strong>{t('OriginForm.Parent_Label')}:</strong> {parents.find(g => g.entity_id === origin.parent_id)?.name}</Typography>
                    <Typography variant="body2"><strong>{t('PlatesList.group')}:</strong> {originTypes.find(g => g.origin_type_id === origin.origin_type_id)?.name}</Typography>
                    
                    <Box display="flex" alignItems="center">
                        <Typography variant="body2"><strong>{t('OriginsListMobile.is_enabled')}: </strong></Typography>
                        <Checkbox checked={origin.is_enabled} disabled size="small" />
                    </Box>
                    <Box display="flex" alignItems="center">
                        <Typography variant="body2"><strong>{t('OriginsListMobile.success')}:</strong></Typography>
                        <Checkbox checked={origin.success} disabled size="small" />
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
    
    return (
  
        <Box sx={{ height: 400, width: '100%', p: isMobile ? 1 : 0 }}>
            
            {DialogComponent} {/* for 'confirm' dialog*/}
            
            <Stack 
                direction={isMobile ? "column" : "row"} 
                spacing={2} 
                sx={{ mb: 2 }} 
                alignItems="center"
            >
                <Button 
                    fullWidth={isMobile}
                    variant="contained" 
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenModal(null)}
                >
                    {t('OriginsList.Add_New_Origin')}
                </Button>
                <Typography >{t('OriginsList.total_enabled')} {totalEnabled}</Typography>
            </Stack>

            {/* Основной контент: Сетка или Карточки */}
            {isMobile ? (
                <Box sx={{ pb: 2 }}>
                    {data.map(plate => renderMobileCard(plate))}
                </Box>
            ) : (
                <LocalizedGrid
                    lang={i18n.language as any}
                    gridId='OriginsListMobile'
                    rows={data}
                    columns={columns}
                    getRowId={(row) => row.entity_id}
                    hideToolbar={true}
                />
            )}

            <Typography color='error'>{error?.message}</Typography>

            <FormModal open={isModalOpen} onClose={handleCloseModal}>
                <OriginForm
                    origin={selectedOrigin}
                    onSave={handleUpdate}
                    onCancel={handleCloseModal}
                    originTypes={originTypes}
                    parents={parents}
                />
                {/* <Typography>Modal content goes here.</Typography> */}
            </FormModal>

            <FormModal open={isImportOpen} onClose={handleCloseImport}>
                <ImportForm
                    origin={selectedOrigin}
                    onClose={handleCloseImport}
                />
                {/* <Typography>Modal content goes here.</Typography> */}
            </FormModal>
        </Box>
  
    );
};